import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type DecisionQuestionView } from '@apm/contracts';
import { Dialog } from '../../../components/ui/dialog';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { decisionSheetApi, decisionSheetKeys } from '../api';
import { optionTitle, stampDateTime } from '../model';

/**
 * Lịch sử chốt của một câu — ai chọn gì, lúc nào (APM-DSHEET-b đọc từ event).
 *
 * Mỗi dòng có "Xem sổ lúc này": mở cả sổ tại đúng mốc ấy (`asOf`, luật #7) —
 * câu hỏi thật sau "ai đổi B thành D" thường là "lúc đó các câu khác ra sao".
 */
export function AnswerHistoryDialog({
  sheetId,
  question,
  onViewAt,
  onClose,
}: {
  readonly sheetId: string;
  readonly question: DecisionQuestionView;
  readonly onViewAt?: ((at: string) => void) | undefined;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const history = useQuery({
    queryKey: decisionSheetKeys.history(sheetId, question.id),
    queryFn: () => decisionSheetApi.history(sheetId, question.id),
  });

  return (
    <Dialog label={t('decisionSheet.historyTitle', { code: question.code })} onClose={onClose}>
      <h2 className="text-lg font-semibold">
        {t('decisionSheet.historyTitle', { code: question.code })}
      </h2>
      <p className="text-sm text-muted-foreground">{question.title}</p>

      {history.isPending ? (
        <Skeleton className="h-24" />
      ) : history.isError ? (
        <ErrorState
          message={errorMessage(history.error, t)}
          onRetry={() => void history.refetch()}
        />
      ) : history.data.entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('decisionSheet.historyEmpty')}</p>
      ) : (
        <ol className="flex flex-col gap-2">
          {history.data.entries.map((entry, index) => (
            <li
              key={`${entry.at}-${index}`}
              className="flex flex-col gap-0.5 rounded-DEFAULT border border-border px-3 py-2 text-sm"
            >
              <span className="font-medium [overflow-wrap:anywhere]">
                {entry.key
                  ? t('decisionSheet.historyChose', {
                      key: entry.key,
                      title: optionTitle(question, entry.key, t('decisionSheet.other')),
                    })
                  : t('decisionSheet.historyCleared')}
              </span>
              {entry.note ? (
                <span className="whitespace-pre-line [overflow-wrap:anywhere]">{entry.note}</span>
              ) : null}
              <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                <span>{stampDateTime(entry.at)}</span>
                <span>·</span>
                <span>
                  {entry.imported
                    ? t('decisionSheet.historyImported')
                    : (entry.by?.name ?? t('decisionSheet.someone'))}
                </span>
                {onViewAt ? (
                  <button
                    type="button"
                    className="ml-auto underline-offset-2 hover:underline"
                    onClick={() => onViewAt(entry.at)}
                  >
                    {t('decisionSheet.historyViewAt')}
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Dialog>
  );
}
